import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from "@mui/material";
import { LineChart, BarChart } from "@mui/x-charts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useGetDashboardStatsQuery } from "store/services/dashboardApi";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAuth } from "hooks";
import moment from "moment";
import { ZoomableBox, ChartTypeToggle, ChartValueMarkElement, TopBarLabel, barValueLabel, formatCompactNumber, downloadChartSvg } from "./ChartControls";

const TIME_PERIODS = [
  { value: "week", label: "Last 7 Days" },
  { value: "month", label: "Last 30 Days" },
  { value: "custom", label: "Custom Date Range" },
];

const ShipmentChartDetails = () => {
  const { isEntityAdmin, isAccountAdmin, isSystemAdmin } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState("month");
  const [chartType, setChartType] = useState("bar");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const chartRef = useRef(null);
  const exportRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuTimeout = useRef(null);
  const isDeviceLandscape = useMediaQuery(
    "(orientation: landscape) and (max-height: 650px), (orientation: landscape) and (hover: none) and (pointer: coarse) and (max-width: 1200px)"
  );
  const isLandscapeMode = isDeviceLandscape || isFullScreen;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartMargin = isMobile
    ? { left: 40, right: 20, top: 46, bottom: 78 }
    : { left: 55, right: 48, top: 46, bottom: 78 };

  // Set initial date range on mount (30 days for "month")
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29);
    setFromDate(thirtyDaysAgo);
    setToDate(today);
  }, []);

  // Update date range based on timePeriod or custom dates
  useEffect(() => {
    const today = new Date();
    if (timePeriod === "week") {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 6);
      setFromDate(sevenDaysAgo);
      setToDate(today);
    } else if (timePeriod === "month") {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 29);
      setFromDate(thirtyDaysAgo);
      setToDate(today);
    } else if (timePeriod === "custom" && customFrom && customTo) {
      setFromDate(customFrom);
      setToDate(customTo);
    }
  }, [timePeriod, customFrom, customTo]);

  // Format dates for API call
  const formattedFrom = fromDate
    ? moment(fromDate).format("YYYY-MM-DD")
    : undefined;
  const formattedTo = toDate ? moment(toDate).format("YYYY-MM-DD") : undefined;

  // Fetch data with skip condition
  const {
    data: shipmentStats = {},
    isLoading,
    isError,
    refetch,
  } = useGetDashboardStatsQuery(
    {
      fromDate: formattedFrom,
      toDate: formattedTo,
    },
    { skip: !formattedFrom || !formattedTo }
  );

  // Trigger refetch when dates change
  useEffect(() => {
    if (formattedFrom && formattedTo) {
      refetch();
    }
  }, [formattedFrom, formattedTo, refetch]);

  useEffect(() => {
    if (open) {
      clearTimeout(menuTimeout.current);
      menuTimeout.current = setTimeout(() => {
        setAnchorEl(null);
      }, 5000);
    }
    return () => clearTimeout(menuTimeout.current);
  }, [open]);

  useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsFullScreen(false);
        try {
          if (window.screen?.orientation?.unlock) {
            window.screen.orientation.unlock();
          }
        } catch (e) {}
      }
    };
    document.addEventListener("fullscreenchange", exitHandler);
    document.addEventListener("webkitfullscreenchange", exitHandler);
    return () => {
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("webkitfullscreenchange", exitHandler);
    };
  }, []);

  // Lock page scroll while landscape overlay is active and restore on exit
  useEffect(() => {
    if (isLandscapeMode) {
      const scrollY = window.scrollY;
      const originalBody = document.body.style.cssText;
      const originalHtml = document.documentElement.style.cssText;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.cssText = originalBody;
        document.documentElement.style.cssText = originalHtml;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isLandscapeMode]);

  const shipmentStatsData = shipmentStats.ShipmentLineResult || [];
  const shipmentCompletionStatsData = shipmentStats.DeliveryLineResult || [];

  // Auth check after all hooks
  const hasAccess = isEntityAdmin || isAccountAdmin || isSystemAdmin;
  if (!hasAccess) {
    return <div>Access Denied. Admin privileges required.</div>;
  }

  // Helper functions for date range and formatting (inspired by ShipmentLineChart)
  const generateDateRange = (period, start, end) => {
    const dates = [];
    if (period === "custom" && start && end) {
      let currentDate = new Date(start);
      while (currentDate <= end) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (period === "week") {
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push(date);
      }
    } else if (period === "month") {
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push(date);
      }
    }
    return dates;
  };

  const getDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Generate complete date range
  const allDates = generateDateRange(timePeriod, fromDate, toDate);
  const dateLabels = allDates.map((date) => formatDate(date));

  // Create lookup maps for API data
  const createdShipmentsMap = new Map();
  shipmentStatsData.forEach((entry) => {
    const date = new Date(entry.date);
    const key = getDateKey(date);
    createdShipmentsMap.set(key, entry.totalShipment);
  });

  const deliveredShipmentsMap = new Map();
  shipmentCompletionStatsData.forEach((entry) => {
    const date = new Date(entry.date);
    const key = getDateKey(date);
    deliveredShipmentsMap.set(key, entry.totalShipment);
  });

  // Map to complete date range
  const createdCounts = allDates.map(
    (date) => createdShipmentsMap.get(getDateKey(date)) || 0
  );
  const deliveredCounts = allDates.map(
    (date) => deliveredShipmentsMap.get(getDateKey(date)) || 0
  );

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
    if (event.target.value !== "custom") {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    clearTimeout(menuTimeout.current);
    menuTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 3000);
  };

  const handleMenuClose = () => {
    clearTimeout(menuTimeout.current);
    setAnchorEl(null);
  };



  const handleDownload = (format) => {
    downloadChartSvg(exportRef.current, format, "shipment_details_chart");
    handleMenuClose();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) return <div>Loading chart...</div>;
  if (isError) return <div>Failed to load data</div>;

  const renderChart = (w, h) =>
    chartType === "bar" ? (
      <BarChart
        sx={{ width: "100%", height: "100%" }}
        {...(w ? { width: w, height: h } : {})}
        xAxis={[
          {
            scaleType: "band",
            data: dateLabels,
            tickLabelStyle: {
              angle: -45,
              textAnchor: "end",
              fontSize: isMobile ? 9 : 11,
            },
          },
        ]}
        margin={chartMargin}
        barLabel={barValueLabel}
        slots={{ barLabel: TopBarLabel }}
        yAxis={[
          {
            min: 0,
            valueFormatter: formatCompactNumber,
            max: Math.ceil(Math.max(...createdCounts, ...deliveredCounts, 10) * 1.25),
          },
        ]}
        series={[
          { data: createdCounts, label: "Created Shipments", color: "rgba(255,0,0,0.7)" },
          { data: deliveredCounts, label: "Delivered Shipments", color: "#008000" },
        ]}
      />
    ) : (
      <LineChart
        sx={{ width: "100%", height: "100%" }}
        {...(w ? { width: w, height: h } : {})}
        xAxis={[
          {
            scaleType: "point",
            data: dateLabels,
            tickLabelStyle: {
              angle: -45,
              textAnchor: "end",
              fontSize: isMobile ? 9 : 11,
            },
          },
        ]}
        margin={chartMargin}
        yAxis={[
          {
            min: 0,
            valueFormatter: formatCompactNumber,
            max: Math.ceil(Math.max(...createdCounts, ...deliveredCounts, 10) * 1.25),
          },
        ]}
        series={[
          { id: "createdShipments", data: createdCounts, label: "Created Shipments", color: "rgba(255,0,0,0.7)", strokeWidth: 3, showMark: true },
          { id: "deliveredShipments", data: deliveredCounts, label: "Delivered Shipments", color: "#008000", strokeWidth: 2, showMark: true },
        ]}
        slots={{ mark: ChartValueMarkElement }}
      />
    );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={{ xs: 1, sm: 2 }}
        >
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
        </Box>

        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={{ xs: 2, sm: 4 }}>
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              {TIME_PERIODS.map((period) => (
                <MenuItem key={period.value} value={period.value}>
                  {period.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {timePeriod === "custom" && (
            <>
              <DatePicker
                label="From Date"
                value={customFrom}
                minDate={customTo ? (() => { const d = new Date(customTo); d.setMonth(d.getMonth() - 3); return d; })() : undefined}
                maxDate={customTo || new Date()}
                onChange={(newValue) => {
                  setCustomFrom(newValue);
                  if (newValue && customTo) {
                    refetch();
                  }
                }}
                PopperProps={{ container: chartRef.current }}
                renderInput={(params) => (
                  <TextField {...params} sx={{ mr: 2 }} />
                )}
              />
              <DatePicker
                label="To Date"
                value={customTo}
                minDate={customFrom || (() => { const d = new Date(customFrom); d.setMonth(d.getMonth() - 3); return d; })()}
                maxDate={(() => { const d = new Date(customFrom); d.setMonth(d.getMonth() + 3); const today = new Date(); return d < today ? d : today; })()}
                onChange={(newValue) => {
                  setCustomTo(newValue);
                  if (customFrom && newValue) {
                    refetch();
                  }
                }}
                PopperProps={{ container: chartRef.current }}
                renderInput={(params) => <TextField {...params} />}
              />
            </>
          )}
        </Box>

        <Box
          ref={chartRef}
          sx={{
            width: isLandscapeMode ? "100vw" : "100%",
            height: isLandscapeMode ? "100vh" : "auto",
            minHeight: isLandscapeMode ? "100vh" : { xs: 390, sm: 500 },
            padding: isLandscapeMode ? 1.5 : 2,
            boxShadow: isLandscapeMode ? 0 : 2,
            borderRadius: isLandscapeMode ? 0 : 2,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            position: isLandscapeMode ? "fixed" : "relative",
            ...(isLandscapeMode && {
              top: 0,
              left: 0,
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }),
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={{ xs: 1, sm: 2 }}
          >
            <Typography
              variant="p"
              sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
            >
              Shipment Trends
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <ChartTypeToggle
                value={chartType}
                onChange={(_, value) => value && setChartType(value)}
              />
              <IconButton onClick={handleMenuClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleDownload("png")}>
                  <GetAppIcon sx={{ mr: 1 }} /> Download PNG
                </MenuItem>
                <MenuItem onClick={() => handleDownload("jpeg")}>
                  <GetAppIcon sx={{ mr: 1 }} /> Download JPEG
                </MenuItem>
                <MenuItem onClick={() => handleDownload("pdf")}>
                  <PictureAsPdfIcon sx={{ mr: 1 }} /> Download PDF
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <ZoomableBox scrollable sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: isLandscapeMode ? "calc(100vh - 90px)" : { xs: 350, sm: 400 }, minHeight: isLandscapeMode ? "calc(100vh - 90px)" : { xs: 350, sm: 360 } }}>
              {renderChart()}
            </Box>
          </ZoomableBox>
        </Box>

        <Box
          ref={exportRef}
          aria-hidden
          sx={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }}
        >
          {renderChart(1800, 800)}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ShipmentChartDetails;
